// src/pages/wizard/Step2Authenticate.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Key,
  Lock,
  Globe,
  Mail,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Shield,
} from 'lucide-react';
import { Input, Label } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { apiCall } from '../../utils/api';
import { API_BASE_URL } from '../../config';

// API base for OAuth start URLs (popup needs absolute URL)
const API_BASE = API_BASE_URL.replace(/\/$/, '');


const OAUTH_POLL_INTERVAL_MS = 2000;
const OAUTH_POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 720;

// Connectors that signal completion via postMessage from the popup.
// All current OAuth callbacks now use this pattern after the unification fix.
const POSTMESSAGE_CONNECTORS = new Set(['github', 'slack', 'jira', 'teams', 'namely']);


export default function Step2Authenticate({
  connector,
  auth,
  configId,
  authMethod,
  authComplete,
  onAuthChange,
  onConfigCreated,
  onAuthMethodChange,
  onAuthComplete,
}) {
  const connectorId = connector?.id || 'github';
  const connectorName = connector?.name || 'Connector';

  // Connectors with no OAuth flow must start (and stay) in PAT mode.
  const PAT_ONLY_CONNECTORS = new Set(['club_automation', 'jazzhr']);
  const defaultAuthMode = PAT_ONLY_CONNECTORS.has(connectorId)
    ? 'pat'
    : (authMethod || 'oauth');

  // UI mode: 'oauth' (default) or 'pat'
  const [authMode, setAuthMode] = useState(defaultAuthMode);

  // OAuth flow state
  const [creating, setCreating] = useState(false);
  const [oauthInProgress, setOauthInProgress] = useState(false);
  const [oauthError, setOauthError] = useState(null);
  const [oauthDetails, setOauthDetails] = useState(null);

  // PAT flow state (existing)
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Refs for popup management
  const popupRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const popupCloseCheckRef = useRef(null);

  // ─────────────────────────────────────────────────────────────
  // Cleanup helpers
  // ─────────────────────────────────────────────────────────────
  const clearOauthTimers = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (popupCloseCheckRef.current) {
      clearInterval(popupCloseCheckRef.current);
      popupCloseCheckRef.current = null;
    }
  }, []);

  const closePopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      try { popupRef.current.close(); } catch (e) { /* ignore */ }
    }
    popupRef.current = null;
  }, []);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      clearOauthTimers();
      closePopup();
    };
  }, [clearOauthTimers, closePopup]);

  // Sync authMode back up to parent when it changes
  useEffect(() => {
    if (onAuthMethodChange && authMethod !== authMode) {
      onAuthMethodChange(authMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authMode]);

  // ─────────────────────────────────────────────────────────────
  // OAuth: start flow
  // ─────────────────────────────────────────────────────────────
  const handleOauthStart = async () => {
    setOauthError(null);
    setOauthDetails(null);

    // Namely's API and OAuth are per-tenant (https://{subdomain}.namely.com),
    // so the subdomain must be known before the popup can be opened.
    if (connectorId === 'namely' && !(auth?.subdomain || '').trim()) {
      setOauthError('Enter your Namely subdomain first (the {company} in {company}.namely.com).');
      return;
    }

    let activeConfigId = configId;

    // Step 1: Create the config row if we don't already have one
    if (!activeConfigId) {
      setCreating(true);
      try {
        const displayName = `${connectorName} · ${new Date().toISOString().slice(0, 10)}`;
        const res = await apiCall('/sync/configs', {
          method: 'POST',
          body: JSON.stringify({
            connector_key: connectorId,
            display_name: displayName,
            config_json: {
              connector_key: connectorId,
              ...(connectorId === 'namely' && auth?.subdomain
                ? { subdomain: auth.subdomain.trim().toLowerCase() }
                : {}),
            },
          }),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to create connector config');
        }

        const data = await res.json();
        activeConfigId = data.id;
        onConfigCreated && onConfigCreated(activeConfigId);
      } catch (err) {
        setOauthError(err.message || 'Failed to create connector config');
        setCreating(false);
        return;
      }
      setCreating(false);
    }

    // Namely: make sure the (possibly re-entered) subdomain is saved on the
    // config before OAuth starts, including the reconnect path where the
    // config already existed and the create branch above was skipped.
    if (connectorId === 'namely' && activeConfigId && auth?.subdomain) {
      try {
        await apiCall(`/sync/configs/${activeConfigId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            config_json: { subdomain: auth.subdomain.trim().toLowerCase() },
          }),
        });
      } catch (err) {
        setOauthError('Failed to save the Namely subdomain. Please try again.');
        return;
      }
    }

    // Step 2: Mint a short-lived OAuth ticket so the popup can authenticate
    // (popup cannot attach Bearer tokens via window.open).
    let ticket;
    try {
      const ticketRes = await apiCall('/oauth/ticket', { method: 'POST' });
      if (!ticketRes.ok) {
        const e = await ticketRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to mint OAuth ticket');
      }
      const ticketData = await ticketRes.json();
      ticket = ticketData.ticket;
    } catch (err) {
      setOauthError(err.message || 'Failed to prepare OAuth flow');
      return;
    }

    // Step 3: Open OAuth popup with config_id and ticket
    let startUrl = `${API_BASE}/oauth/${connectorId}/start?config_id=${activeConfigId}&ticket=${encodeURIComponent(ticket)}`;
    if (connectorId === 'namely' && auth?.subdomain) {
      startUrl += `&subdomain=${encodeURIComponent(auth.subdomain.trim().toLowerCase())}`;
    }
    const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
    const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
    const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`;

    const popup = window.open(startUrl, `oauth_${connectorId}`, features);


    if (!popup) {
      setOauthError(
        'Popup was blocked by your browser. Please allow popups for this site and try again.'
      );
      return;
    }

    popupRef.current = popup;
    setOauthInProgress(true);

    // Track when the popup closes so we can stop polling and let the user retry
    let popupClosedAt = null;
    popupCloseCheckRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        if (popupClosedAt === null) {
          popupClosedAt = Date.now();
        }

        if (POSTMESSAGE_CONNECTORS.has(connectorId)) {
          // GitHub: postMessage should have arrived. Brief grace period in case
          // the message is in-flight, then cancel.
          setTimeout(() => {
            if (oauthInProgress && !oauthDetails) {
              handleOauthCancelled();
            }
          }, 500);
        } else {
          // Jira/Slack/Teams: poll a few more cycles in case the popup closed
          // immediately after writing tokens. After ~6 seconds with no success,
          // give up and let the user retry.
          if (Date.now() - popupClosedAt > 6000) {
            handleOauthCancelled();
          }
        }
      }
    }, 1000);


    // Step 3: Schedule timeout
    pollTimeoutRef.current = setTimeout(() => {
      handleOauthTimeout();
    }, OAUTH_POLL_TIMEOUT_MS);

    // Step 4: Start polling for non-postMessage connectors
    if (!POSTMESSAGE_CONNECTORS.has(connectorId)) {
      pollIntervalRef.current = setInterval(() => {
        pollOauthStatus(activeConfigId);
      }, OAUTH_POLL_INTERVAL_MS);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // OAuth: postMessage listener (GitHub)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!oauthInProgress || !POSTMESSAGE_CONNECTORS.has(connectorId)) return;

    const handleMessage = (event) => {
      // Validate origin: only accept messages from our backend or own frontend.
      // State nonce is primary defense; origin check prevents third-party spoofing.
      if (event.origin !== API_BASE && event.origin !== window.location.origin) return;

      const data = event.data;
      if (!data || typeof data !== 'object') return;

      const expectedType = `${connectorId}_oauth_success`;
      if (data.type === expectedType && data.config_id) {
        // Success: validate by hitting the test endpoint
        finalizeOauthSuccess(data.config_id);
      } else if (data.type === `${connectorId}_oauth_error`) {
        setOauthError(data.message || 'OAuth failed');
        cleanupAfterOauth();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [oauthInProgress, connectorId]);

  // ─────────────────────────────────────────────────────────────
  // OAuth: poll backend for completion (Jira, Slack, Teams)
  // ─────────────────────────────────────────────────────────────
  const pollOauthStatus = async (cfgId) => {
    try {
      const res = await apiCall(`/oauth/${connectorId}/test?config_id=${cfgId}`);
      if (!res.ok) return; // Not yet connected, keep polling
      const data = await res.json();
      if (data.ok) {
        finalizeOauthSuccess(cfgId, data);
      }
    } catch (err) {
      // Polling errors are normal until OAuth completes; ignore
    }
  };

  // ─────────────────────────────────────────────────────────────
  // OAuth: shared success path
  // ─────────────────────────────────────────────────────────────
  const finalizeOauthSuccess = async (cfgId, prefetchedTestData = null) => {
    clearOauthTimers();
    closePopup();

    let testData = prefetchedTestData;
    if (!testData) {
      try {
        const res = await apiCall(`/oauth/${connectorId}/test?config_id=${cfgId}`);
        if (res.ok) testData = await res.json();
      } catch (err) {
        // Test failed but OAuth succeeded; show generic success
      }
    }

    setOauthInProgress(false);
    setOauthDetails(testData || { ok: true });
    setOauthError(null);
    onAuthComplete && onAuthComplete(true);
  };

  const handleOauthCancelled = () => {
    cleanupAfterOauth();
    setOauthError('OAuth was cancelled. Click "Connect" to try again.');
  };

  const handleOauthTimeout = () => {
    cleanupAfterOauth();
    setOauthError('OAuth timed out. Click "Connect" to try again.');
  };

  const cleanupAfterOauth = () => {
    clearOauthTimers();
    closePopup();
    setOauthInProgress(false);
  };

  // ─────────────────────────────────────────────────────────────
  // OAuth: disconnect / reset
  // ─────────────────────────────────────────────────────────────
  const handleOauthReset = () => {
    setOauthDetails(null);
    setOauthError(null);
    onAuthComplete && onAuthComplete(false);
    // Note: we keep configId so reconnecting writes to the same row.
    // We do NOT delete the config here; that's a destructive action.
  };

  // ─────────────────────────────────────────────────────────────
  // PAT flow (preserved from previous version)
  // ─────────────────────────────────────────────────────────────
  const updateField = (field, value) => {
    onAuthChange({ ...auth, [field]: value });
    setTestResult(null);
  };

  const canTestPat = () => {
    switch (connectorId) {
      case 'github': return auth.org && auth.token;
      case 'jira': return auth.site && auth.email && auth.token;
      case 'slack': return auth.token;
      case 'teams': return auth.tenantId && auth.clientId && auth.clientSecret;
      case 'club_automation': return auth.clientId && auth.clientSecret && auth.scope;
      case 'jazzhr': return auth.apiKey;
      case 'namely': return auth.subdomain && auth.token;
      default: return false;
    }
  };

  const handleTestPat = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const testPayload = buildTestPayload(connectorId, auth);
      const res = await apiCall(`/sync/test-${connectorId}`, {
        method: 'POST',
        body: JSON.stringify(testPayload),
      });
      const data = await res.json();

      if (data.ok) {
        // PAT validated. Create config if needed, then ALWAYS (re)write the
        // credentials to secrets_vault. Splitting/secret-write must happen
        // for an EXISTING config too (reconnecting / re-entering a key),
        // not only on first creation - otherwise the vault never gets the
        // secret and sync later fails with "credentials missing".
        let activeConfigId = configId;
        const { configFields, secretFields } = splitPatAuth(connectorId, auth);

        if (!activeConfigId) {
          // Non-credential fields stay in config_json; credentials go to
          // secrets_vault (encrypted at rest) below.
          const displayName = `${connectorName} · ${new Date().toISOString().slice(0, 10)}`;
          const createRes = await apiCall('/sync/configs', {
            method: 'POST',
            body: JSON.stringify({
              connector_key: connectorId,
              display_name: displayName,
              config_json: {
                ...configFields,
                connector_key: connectorId,
                auth_method: 'pat',
              },
            }),
          });
          if (!createRes.ok) {
            const e = await createRes.json().catch(() => ({}));
            throw new Error(e.detail || 'Failed to create connector config');
          }
          const created = await createRes.json();
          activeConfigId = created.id;
          onConfigCreated && onConfigCreated(activeConfigId);
        }

        // ALWAYS write credentials to the vault for the active config,
        // whether it was just created or already existed. The secrets
        // endpoint upserts, so re-writing an existing key is safe.
        if (activeConfigId && Object.keys(secretFields).length > 0) {
          const secretsRes = await apiCall(
            `/sync/configs/${activeConfigId}/secrets`,
            {
              method: 'POST',
              body: JSON.stringify({ secrets: secretFields }),
            }
          );
          if (!secretsRes.ok) {
            const e = await secretsRes.json().catch(() => ({}));
            throw new Error(e.detail || 'Failed to store credentials');
          }
        }

        setTestResult({ ok: true, message: buildSuccessMessage(connectorId, data) });
        onAuthComplete && onAuthComplete(true);

      } else {
        setTestResult({ ok: false, message: data.detail || 'Connection failed' });
        onAuthComplete && onAuthComplete(false);
      }
    } catch (err) {
      setTestResult({ ok: false, message: err.message || 'Failed to test connection' });
      onAuthComplete && onAuthComplete(false);
    } finally {
      setTesting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-2">
          Authenticate {connectorName}
        </h2>
        <p className="text-sm text-[#a0a0a0]">
          {authMode === 'oauth'
            ? `Connect securely to your ${connectorName} account.`
            : `Provide an access token to connect to your ${connectorName} account.`}
        </p>
      </div>

      <div className="max-w-2xl space-y-6">
        {authMode === 'oauth' ? (
          <>
            {connectorId === 'namely' && !authComplete && (
              <PatField
                label="Namely Subdomain *"
                icon={Globe}
                placeholder="e.g., yourcompany (from yourcompany.namely.com)"
                value={auth.subdomain || ''}
                onChange={(v) => updateField('subdomain', v)}
                help="Your company's Namely subdomain. OAuth signs in through your company's own Namely site."
              />
            )}
            <OauthPanel
              connectorId={connectorId}
              connectorName={connectorName}
              creating={creating}
              oauthInProgress={oauthInProgress}
              oauthError={oauthError}
              oauthDetails={oauthDetails}
              authComplete={authComplete}
              onStart={handleOauthStart}
              onReset={handleOauthReset}
              canStart={connectorId !== 'namely' || !!(auth.subdomain || '').trim()}
            />
          </>
        ) : (
          <PatPanel
            connectorId={connectorId}
            auth={auth}
            updateField={updateField}
            testing={testing}
            testResult={testResult}
            canTest={canTestPat()}
            onTest={handleTestPat}
          />
        )}

        {/* Mode toggle */}
        {!PAT_ONLY_CONNECTORS.has(connectorId) && (
          <div className="pt-4 border-t border-[#2a2a2a]">
            <button
              type="button"
              onClick={() => {
                const next = authMode === 'oauth' ? 'pat' : 'oauth';
                setAuthMode(next);
                setOauthError(null);
                setTestResult(null);
                onAuthComplete && onAuthComplete(false);
              }}
              className="text-sm text-[#a0a0a0] hover:text-white transition-colors"
              disabled={oauthInProgress || testing}
            >
              {authMode === 'oauth'
                ? 'Use access token instead'
                : 'Connect with OAuth instead'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// OAuth Panel
// ============================================================
function OauthPanel({
  connectorId,
  connectorName,
  creating,
  oauthInProgress,
  oauthError,
  oauthDetails,
  authComplete,
  onStart,
  onReset,
  canStart = true,
}) {
  // Connected state
  if (authComplete && oauthDetails) {
    return (
      <div className="space-y-4">
        <div className="p-5 rounded-lg border border-green-500/20 bg-green-500/10">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-base font-semibold text-green-400">
                Connected to {connectorName}
              </p>
              <SuccessDetails connectorId={connectorId} details={oauthDetails} />
            </div>
          </div>
        </div>
        <Button variant="secondary" onClick={onReset}>
          Disconnect and reconnect
        </Button>
      </div>
    );
  }

  // In-progress state
  if (oauthInProgress) {
    return (
      <div className="p-6 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a]">
        <div className="flex items-center gap-3 mb-3">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
          <p className="text-sm font-medium text-white">
            Waiting for {connectorName} authentication...
          </p>
        </div>
        <p className="text-xs text-[#6a6a6a]">
          Complete the sign-in in the popup window. This page will update
          automatically when finished.
        </p>
      </div>
    );
  }

  // Default: connect button
  return (
    <div className="space-y-4">
      <div className="p-5 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a]">
        <div className="flex items-start gap-3 mb-4">
          <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-white mb-1">
              Secure OAuth connection
            </p>
            <p className="text-xs text-[#a0a0a0]">
              You'll be redirected to {connectorName} to grant access. Connex
              never sees your password and only requests the permissions
              required for the entities you choose.
            </p>
          </div>
        </div>

        <Button
          onClick={onStart}
          disabled={creating || !canStart}
          className="w-full"
        >
          {creating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Preparing connection...
            </>
          ) : (
            <>
              <ExternalLink className="w-4 h-4" />
              Connect with {connectorName}
            </>
          )}
        </Button>
      </div>

      {oauthError && (
        <div className="p-4 rounded-lg border border-red-500/20 bg-red-500/10 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-400">Connection failed</p>
            <p className="text-sm text-red-400/80 mt-1">{oauthError}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Success details (per connector)
// ============================================================
function SuccessDetails({ connectorId, details }) {
  const rows = extractDetailRows(connectorId, details);
  if (rows.length === 0) return null;

  return (
    <dl className="mt-3 space-y-1.5">
      {rows.map((row) => (
        <div key={row.label} className="flex items-baseline gap-3 text-sm">
          <dt className="text-[#6a6a6a] min-w-[120px]">{row.label}</dt>
          <dd className="text-white font-medium">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function extractDetailRows(connectorId, d) {
  if (!d) return [];
  switch (connectorId) {
    case 'github':
      return [
        d.whoami_login && { label: 'Account', value: d.whoami_login },
        d.org && { label: 'Organization', value: d.org },
        d.repos_preview_count != null && {
          label: 'Repositories',
          value: `${d.repos_preview_count} found`,
        },
      ].filter(Boolean);
    case 'jira':
      return [
        (d.user_display_name || d.user_email) && {
          label: 'Account',
          value: d.user_display_name || d.user_email,
        },
        d.site_name && { label: 'Site', value: d.site_name },
        d.projects_preview_count != null && {
          label: 'Projects',
          value: `${d.projects_preview_count} found`,
        },
      ].filter(Boolean);
    case 'slack':
      return [
        d.workspace_name && { label: 'Workspace', value: d.workspace_name },
        d.bot_user && { label: 'Bot user', value: d.bot_user },
        d.channels_preview_count != null && {
          label: 'Channels',
          value: `${d.channels_preview_count} found`,
        },
      ].filter(Boolean);
    case 'teams':
      return [
        d.tenant_name && { label: 'Tenant', value: d.tenant_name },
        d.user_display_name && { label: 'Account', value: d.user_display_name },
        d.teams_preview_count != null && {
          label: 'Teams',
          value: `${d.teams_preview_count} found`,
        },
      ].filter(Boolean);
    default:
      return [];
  }
}

// ============================================================
// PAT Panel (preserved from previous version)
// ============================================================
function PatPanel({ connectorId, auth, updateField, testing, testResult, canTest, onTest }) {
  return (
    <div className="space-y-6">
      {connectorId === 'github' && (
        <>
          <PatField
            label="GitHub Organization *"
            icon={Globe}
            placeholder="e.g., connexbi"
            value={auth.org || ''}
            onChange={(v) => updateField('org', v)}
            help="The GitHub organization or user account to sync"
          />
          <PatField
            label="Personal Access Token *"
            icon={Key}
            type="password"
            placeholder="ghp_..."
            value={auth.token || ''}
            onChange={(v) => updateField('token', v)}
            help="Create a token at Settings, Developer settings, Personal access tokens"
          />
        </>
      )}

      {connectorId === 'jira' && (
        <>
          <PatField
            label="Jira Site URL *"
            icon={Globe}
            placeholder="https://yourcompany.atlassian.net"
            value={auth.site || ''}
            onChange={(v) => updateField('site', v)}
          />
          <PatField
            label="Email Address *"
            icon={Mail}
            type="email"
            placeholder="admin@yourcompany.com"
            value={auth.email || ''}
            onChange={(v) => updateField('email', v)}
          />
          <PatField
            label="API Token *"
            icon={Key}
            type="password"
            placeholder="Your Jira API token"
            value={auth.token || ''}
            onChange={(v) => updateField('token', v)}
            help="Create at Account Settings, Security, API tokens"
          />
        </>
      )}

      {connectorId === 'slack' && (
        <>
          <PatField
            label="Workspace Name"
            icon={Globe}
            placeholder="e.g., ACME Corp"
            value={auth.workspace || ''}
            onChange={(v) => updateField('workspace', v)}
            help="Optional: Your Slack workspace name for reference"
          />
          <PatField
            label="Bot Token *"
            icon={Key}
            type="password"
            placeholder="xoxb-..."
            value={auth.token || ''}
            onChange={(v) => updateField('token', v)}
            help="Create a Slack app and install it to your workspace to get a bot token"
          />
        </>
      )}

      {connectorId === 'club_automation' && (
        <>
          <PatField
            label="Client ID *"
            icon={Lock}
            placeholder="From your Daxko credentials email"
            value={auth.clientId || ''}
            onChange={(v) => updateField('clientId', v)}
          />
          <PatField
            label="Client Secret *"
            icon={Key}
            type="password"
            placeholder="From your Daxko credentials email"
            value={auth.clientSecret || ''}
            onChange={(v) => updateField('clientSecret', v)}
            help="Provided by Daxko when your API credentials were generated"
          />
          <PatField
            label="Scope *"
            icon={Globe}
            placeholder="Your client/customer ID"
            value={auth.scope || ''}
            onChange={(v) => updateField('scope', v)}
            help="The scope value provided alongside your client ID and secret in the Daxko email"
          />
        </>
      )}

      {connectorId === 'jazzhr' && (
        <>
          <PatField
            label="API Key *"
            icon={Key}
            type="password"
            placeholder="Your JazzHR API key"
            value={auth.apiKey || ''}
            onChange={(v) => updateField('apiKey', v)}
            help="In JazzHR, go to Settings, Integrations, API. Requires the Pro plan or above. Visible to Super Admin, Recruiting Admin, or Developer roles."
          />
        </>
      )}

      {connectorId === 'namely' && (
        <>
          <PatField
            label="Namely Subdomain *"
            icon={Globe}
            placeholder="e.g., yourcompany (from yourcompany.namely.com)"
            value={auth.subdomain || ''}
            onChange={(v) => updateField('subdomain', v)}
            help="The {company} part of your {company}.namely.com address"
          />
          <PatField
            label="Personal Access Token *"
            icon={Key}
            type="password"
            placeholder="Your Namely personal access token"
            value={auth.token || ''}
            onChange={(v) => updateField('token', v)}
            help="In Namely, open the API menu, then the Personal Access Tokens tab. Requires an Administrator-level account."
          />
        </>
      )}

      <div className="pt-6 border-t border-[#2a2a2a]">
        <Button
          variant="secondary"
          className="w-full"
          onClick={onTest}
          disabled={!canTest || testing}
        >
          {testing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Testing Connection...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              Test Connection
            </>
          )}
        </Button>
      </div>

      {testResult && (
        <div
          className={`p-4 rounded-lg border flex items-start gap-3 ${
            testResult.ok
              ? 'bg-green-500/10 border-green-500/20'
              : 'bg-red-500/10 border-red-500/20'
          }`}
        >
          {testResult.ok ? (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <p className={`text-sm font-medium ${testResult.ok ? 'text-green-400' : 'text-red-400'}`}>
              {testResult.ok ? 'Connection Successful' : 'Connection Failed'}
            </p>
            <p className={`text-sm mt-1 ${testResult.ok ? 'text-green-400/80' : 'text-red-400/80'}`}>
              {testResult.message}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function PatField({ label, icon: Icon, type = 'text', placeholder, value, onChange, help }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
        <Input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pl-10"
        />
      </div>
      {help && <p className="text-xs text-[#6a6a6a]">{help}</p>}
    </div>
  );
}

// ============================================================
// PAT helpers (preserved from previous version)
// ============================================================
function buildTestPayload(connectorId, auth) {
  switch (connectorId) {
    case 'github':
      return { org: auth.org, token: auth.token };
    case 'jira':
      return { site_url: auth.site, email: auth.email, api_token: auth.token };
    case 'slack':
      return { token: auth.token };
    case 'teams':
      return { tenant_id: auth.tenantId, client_id: auth.clientId, client_secret: auth.clientSecret };
    case 'club_automation':
      return { client_id: auth.clientId, client_secret: auth.clientSecret, scope: auth.scope };
    case 'jazzhr':
      return { api_key: auth.apiKey };
    case 'namely':
      return { subdomain: auth.subdomain, access_token: auth.token };
    default:
      return {};
  }
}

function buildSuccessMessage(connectorId, data) {
  switch (connectorId) {
    case 'github':
      return `Connected as ${data.whoami_login || 'user'}. Found ${data.repos_preview_count || 0} repositories.`;
    case 'jira':
      return `Connected as ${data.user_display_name || data.user_email}. Found ${data.projects_preview_count || 0} projects.`;
    case 'slack':
      return `Connected to ${data.workspace_name || 'workspace'}. Found ${data.channels_preview_count || 0} channels.`;
    case 'teams':
      return `Connected successfully. Found ${data.teams_preview_count || 0} teams.`;
    case 'club_automation':
      return `Connected successfully. Credentials validated${data.scope ? ` for scope ${data.scope}` : ''}.`;
    case 'jazzhr':
      return `Connected successfully. Found ${data.jobs_preview_count || 0} jobs.`;
    case 'namely':
      return `Connected to ${data.subdomain || 'Namely'}. Found ${data.profiles_preview_count || 0} employee profiles.`;
    default:
      return 'Connection successful.';
  }
}


// ============================================================
// PAT credential splitting
// ============================================================
// Separates the auth object into:
//   - configFields: non-credential fields that go in config_json
//   - secretFields: credentials that go in secrets_vault
//
// Each connector has different shapes. The keys in secretFields use
// the SAME names that the OAuth flow stores under, so the runners
// see the same key whether auth came via OAuth or PAT.
function splitPatAuth(connectorId, auth) {
  const k = (connectorId || '').toLowerCase();
  switch (k) {
    case 'github':
      return {
        configFields: { org: auth.org || '' },
        secretFields: { github_access_token: auth.token || '' },
      };
    case 'jira':
      return {
        configFields: {
          site_url: auth.site || '',
          base_url: auth.site || '',
          email: auth.email || '',
        },
        secretFields: {
          jira_access_token: auth.token || '',
        },
      };
    case 'slack':
      return {
        configFields: { workspace: auth.workspace || '' },
        secretFields: { slack_token: auth.token || '' },
      };
    case 'teams':
      return {
        configFields: {
          tenant_id: auth.tenantId || '',
          client_id: auth.clientId || '',
        },
        secretFields: {
          teams_client_secret: auth.clientSecret || '',
        },
      };
      case 'club_automation':
      return {
        configFields: {},
        secretFields: {
          club_automation_client_id: auth.clientId || '',
          club_automation_client_secret: auth.clientSecret || '',
          club_automation_scope: auth.scope || '',
        },
      };
    case 'jazzhr':
      return {
        configFields: {},
        secretFields: {
          jazzhr_api_key: auth.apiKey || '',
        },
      };
    case 'namely':
      return {
        configFields: {
          subdomain: (auth.subdomain || '').trim().toLowerCase(),
        },
        secretFields: {
          namely_access_token: auth.token || '',
        },
      };
    default:
      // Unknown connector: be safe, treat everything as a secret
      return { configFields: {}, secretFields: { ...auth } };
  }
}

